import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Flex,
  Grid,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { randomId } from "@mantine/hooks";
import { ArrowCounterClockwiseIcon, CoinsIcon, PackageIcon, ReceiptIcon, TrashIcon } from "@phosphor-icons/react";
import { forwardRef, memo, useImperativeHandle, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import * as ErpInvoiceApi from "../../apis/erp-invoice";
import { useOptionalBusiness } from "../../pages/sales-invoice/new";
import { currencyFormat } from "../../utils";
import { computeInvoiceTotals, lineTotal, lineVat } from "../../utils/invoice-totals";
import { renderBusinessName } from "../../utils/render-business-name";
import { VariantSelectorDrawer } from "../shared/variant-selector-drawer";
import classes from "./invoice-product-form.module.css";

const CREATE_COL = { actions: 44, index: 36, warehouse: 280, qty: 88, unitPrice: 130, amount: 124, qtyInput: 72 } as const;
const AMEND_COL = { actions: 52, index: 36, warehouse: 160, qty: 84, unitPrice: 140, amount: 124, qtyInput: 56 } as const;

type RowKind = "product" | "additional";

type CombinedRow = {
  _kind: RowKind;
  _sourceIndex: number;
  [key: string]: any;
};

type LineColumn = {
  key: string;
  title: ReactNode;
  width?: number | string;
  align?: "left" | "right" | "center";
  sticky?: boolean;
  render: (record: CombinedRow, index: number) => ReactNode;
};

function variantToLine(v: any) {
  return {
    variantId: v.id,
    parentVariantId: v.variantParentId || v.id,
    warehouseId: v.inventories?.[0]?.warehouseId || v.warehouseId || "",
    quantity: 1,
    price: v.price ?? v.b2bPrice ?? v.distributionPrice ?? v.standardPrice ?? 0,
    image: v.image,
    skuCode: v.skuCode,
    nameMon: v.nameMon,
    unit: v.unit,
    unitId: v.unitId,
    baseUnit: v.baseUnit,
    baseUnitId: v.baseUnitId,
    brand: typeof v.brand === "object" ? v.brand?.name || null : v.brand ?? null,
    optionValueNames: v.optionValueNames,
    inventories: v.inventories,
    vatType: v.vatType,
    vatPercent: v.vatPercent,
    hasTax: v.hasTax,
    taxPercent: v.taxPercent,
    prices: v.prices,
  };
}

function lineIdentity(line: any) {
  return String(line?.variantId ?? line?.id ?? "");
}

function findOriginalLine(line: any, originals: any[]) {
  const key = lineIdentity(line);
  if (!key) return undefined;
  return originals.find((o) => lineIdentity(o) === key);
}

function findOriginalAdditional(line: any, originals: any[]) {
  if (line?.id && !line._isNew) {
    const byId = originals.find((o) => o.id === line.id);
    if (byId) return byId;
  }
  return undefined;
}

export type ChangedField = "qty" | "price" | "warehouse" | "discount";

function getChangedFields(current: any, original: any): ChangedField[] {
  const fields: ChangedField[] = [];
  if (Number(current.quantity ?? 0) !== Number(original.quantity ?? 0)) fields.push("qty");
  if (Number(current.price ?? 0) !== Number(original.price ?? 0)) fields.push("price");
  if ((current.warehouseId ?? "") !== (original.warehouseId ?? "")) fields.push("warehouse");
  if ((current.discountType ?? "") !== (original.discountType ?? "") || Number(current.discountValue ?? 0) !== Number(original.discountValue ?? 0)) {
    fields.push("discount");
  }
  return fields;
}

function lineFieldsChanged(current: any, original: any) {
  return getChangedFields(current, original).length > 0;
}

export type LineMarker = "new" | "changed" | "removed" | null;

export function deriveLineMarker(line: any, originalLines: any[], kind: "product" | "additional" = "product"): LineMarker {
  if (line?._removed) return "removed";
  if (line?._isNew) return "new";
  const original =
    kind === "additional" ? findOriginalAdditional(line, originalLines) : findOriginalLine(line, originalLines);
  if (!original) {
    return line?._isNew ? "new" : null;
  }
  return lineFieldsChanged(line, original) ? "changed" : null;
}

/** Count rows with new / changed / removed markers (for amend header pill). */
export function countAmendLineChanges(
  lines: any[] = [],
  additionalLines: any[] = [],
  originalLines: any[] = [],
  originalAdditionalLines: any[] = [],
) {
  let count = 0;
  for (const line of lines) {
    if (deriveLineMarker(line, originalLines, "product")) count += 1;
  }
  for (const line of additionalLines) {
    if (deriveLineMarker(line, originalAdditionalLines, "additional")) count += 1;
  }
  return count;
}

type InvoiceProductFormProps = {
  form: UseFormReturnType<any>;
  mode?: "create" | "amend";
  hideSummary?: boolean;
  hideAdjustments?: boolean;
  originalLines?: any[];
  originalAdditionalLines?: any[];
};

export type InvoiceProductFormHandle = {
  openAdd: () => void;
  openAddManual: () => void;
};

const InvoiceProductFormInner = forwardRef<InvoiceProductFormHandle, InvoiceProductFormProps>(function InvoiceProductForm(
  { form, mode = "create", hideSummary, hideAdjustments, originalLines = [], originalAdditionalLines = [] },
  ref,
) {
  const { t } = useTranslation("invoice");
  const [action, setAction] = useState<any>([]);
  const businessCtx = useOptionalBusiness();
  const buyerName = businessCtx?.business ? renderBusinessName(businessCtx.business) : null;

  const isAmend = mode === "amend";
  const hideSummaryBlock = hideSummary ?? isAmend;
  const hideAdjustmentsBlock = hideAdjustments ?? isAmend;

  const { data: warehousesData } = useSWR("swr.invoice.warehouses", () => ErpInvoiceApi.warehouses(), { revalidateOnFocus: false });
  const warehouseOptions = ((warehousesData as any[]) || []).map((w: any) => ({ value: w.id, label: w.name }));

  const addManualLine = () => {
    form.setFieldValue("additionalLines", [
      ...(form.values?.additionalLines ?? []),
      {
        id: randomId(),
        nameMon: undefined,
        price: undefined,
        quantity: undefined,
        ...(isAmend ? { _isNew: true } : { isNew: true }),
        image: undefined,
        name: undefined,
        unit: undefined,
        discountType: undefined,
        discountValue: undefined,
        additionalLines: true,
        vatAmount: undefined,
      },
    ]);
  };

  useImperativeHandle(ref, () => ({
    openAdd: () => setAction(["add", form.values]),
    openAddManual: addManualLine,
  }));

  const rowProps = useMemo(
    () => (row: CombinedRow) => {
      const isManual = row._kind === "additional";
      const marker = isManual
        ? deriveLineMarker(row, originalAdditionalLines, "additional")
        : deriveLineMarker(row, originalLines, "product");
      return {
        className: [classes.lineRow, isManual ? classes.manualRow : undefined].filter(Boolean).join(" "),
        ...(marker === "changed" ? { "data-changed": true } : {}),
        ...(marker === "new" ? { "data-new": true } : {}),
        ...(marker === "removed" ? { "data-removed": true } : {}),
      };
    },
    [originalLines, originalAdditionalLines],
  );

  const combinedRows: CombinedRow[] = useMemo(() => {
    const products = (form.values?.lines ?? []).map((line: any, index: number) => ({
      ...line,
      _kind: "product" as const,
      _sourceIndex: index,
    }));
    const manuals = (form.values?.additionalLines ?? []).map((line: any, index: number) => ({
      ...line,
      _kind: "additional" as const,
      _sourceIndex: index,
    }));
    return [...products, ...manuals];
  }, [form.values?.lines, form.values?.additionalLines]);

  const columns = useCombinedHeader({
    form,
    warehouseOptions,
    isAmend,
    originalLines,
    originalAdditionalLines,
    col: isAmend ? AMEND_COL : CREATE_COL,
    onClick: (key, item: CombinedRow) => {
      const idx = item._sourceIndex;
      if (item._kind === "product") {
        if (key === "remove") {
          if (isAmend) {
            form.setFieldValue(`lines.${idx}._removed`, true);
            return;
          }
          form.setFieldValue(
            "lines",
            form.values?.lines?.filter((_: any, index: number) => index !== idx) ?? [],
          );
        }
        if (key === "restore") {
          form.setFieldValue(`lines.${idx}._removed`, false);
        }
        return;
      }
      if (key === "remove") {
        if (isAmend) {
          form.setFieldValue(`additionalLines.${idx}._removed`, true);
          return;
        }
        form.setFieldValue(
          "additionalLines",
          (form.values?.additionalLines ?? []).filter((item2: any) => item2.id !== item.id),
        );
      }
      if (key === "restore") {
        form.setFieldValue(`additionalLines.${idx}._removed`, false);
      }
    },
  });

  const {
    itemsSubtotal,
    additionalSubtotal: additionalSum,
    vatTotal: vatSum,
    taxTotal: taxSum,
    discountAmount: discountSum,
    shippingAmount: shippingSum,
    subtotal,
    grandTotal,
    lineCount: totalLineCount,
  } = computeInvoiceTotals(form.values);

  const originalSubtotal = useMemo(() => {
    if (!isAmend) return null;
    return computeInvoiceTotals({
      lines: originalLines,
      additionalLines: originalAdditionalLines,
      discountType: null,
      discountValue: null,
      shippingAmount: null,
    }).subtotal;
  }, [isAmend, originalLines, originalAdditionalLines]);

  const activeLineCount = useMemo(() => {
    const products = (form.values?.lines ?? []).filter((l: any) => !l?._removed).length;
    const manuals = (form.values?.additionalLines ?? []).filter((l: any) => !l?._removed).length;
    return products + manuals;
  }, [form.values?.lines, form.values?.additionalLines]);

  const hasProductLines = (form.values.lines?.length ?? 0) > 0;
  const hasAdditionalLines = (form.values?.additionalLines?.length ?? 0) > 0;
  const hasAnyRows = hasProductLines || hasAdditionalLines;

  return (
    <Box className={classes.root}>
      <Flex direction="column" style={{ minWidth: 0 }}>
        {!hasAnyRows && !isAmend ? (
          <Paper p="xl" withBorder={false} bg="var(--mantine-color-gray-0)" ta="center" radius="md" mx={20} my={16}>
            <PackageIcon size={40} color="var(--mantine-color-gray-6)" />
            <Text mt="sm" fw={500}>
              {t("productForm.emptyLines")}
            </Text>
            <Text size="xs" c="dimmed">
              {t("productForm.emptyLinesHint")}
            </Text>
          </Paper>
        ) : hasAnyRows ? (
          <Box className={classes.linesTable}>
            <Table
              className={classes.table}
              withTableBorder={false}
              withColumnBorders={false}
              withRowBorders={false}
              highlightOnHover={false}
              striped={false}
              horizontalSpacing={0}
              verticalSpacing={0}
              tabularNums
            >
              <Table.Thead>
                <Table.Tr>
                  {columns.map((col) => (
                    <Table.Th
                      key={col.key}
                      className={[col.sticky ? classes.stickyActions : undefined, col.align === "right" ? classes.thRight : undefined]
                        .filter(Boolean)
                        .join(" ") || undefined}
                      style={
                        col.width != null
                          ? { width: col.width, boxSizing: "border-box" }
                          : undefined
                      }
                    >
                      {col.title}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {combinedRows.map((row, index) => {
                  const props = rowProps(row);
                  return (
                    <Table.Tr key={`${row._kind}-${row.id ?? row.variantId ?? row._sourceIndex}-${index}`} {...props}>
                      {columns.map((col) => (
                        <Table.Td
                          key={col.key}
                          className={col.sticky ? classes.stickyActions : undefined}
                          ta={col.align ?? "left"}
                          style={
                            col.width != null
                              ? { width: col.width, boxSizing: "border-box" }
                              : undefined
                          }
                        >
                          {col.render(row, index)}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            <Box className={classes.linesSubtotal}>
              {isAmend && originalSubtotal != null && originalSubtotal !== subtotal ? (
                <Text span className={classes.linesSubtotalPrev}>
                  {t("amend.subtotalPrevious")}{" "}
                  <Text span className={classes.wasValue}>
                    {currencyFormat(originalSubtotal, { withSymbol: false })}
                  </Text>
                </Text>
              ) : null}
              <Box className={classes.linesSubtotalNow}>
                <Text span className={classes.linesSubtotalLabel}>
                  {t("productForm.linesSubtotalPrefix", { count: isAmend ? activeLineCount : totalLineCount })}
                </Text>
                <Text span className={classes.linesSubtotalAmount}>
                  {currencyFormat(subtotal, { withSymbol: false })}
                </Text>
              </Box>
            </Box>
          </Box>
        ) : null}

        <Alert color="red" title={form.errors.lines} withCloseButton hidden={!form.errors.lines} mx={20} mt="sm">
          {t("productForm.noItems")}
        </Alert>
        {(!hideAdjustmentsBlock || !hideSummaryBlock) && (
          <Grid>
            {!hideAdjustmentsBlock && (
            <Grid.Col span={{ base: 12, md: hideSummaryBlock ? 12 : 7 }}>
              <Box className={hideSummaryBlock ? classes.adjustGrid : undefined} p={hideSummaryBlock ? undefined : "md"}>
                <Box className={hideSummaryBlock ? classes.descCol : undefined}>
                  <Textarea
                    label={t("productForm.description")}
                    placeholder={t("productForm.descriptionPlaceholder")}
                    autosize={false}
                    minRows={4}
                    {...form.getInputProps("description")}
                  />
                </Box>

                <Box className={hideSummaryBlock ? classes.adjustRight : undefined}>
                  <Box>
                    <Text className={classes.fieldLabel} mb={6}>
                      {t("productForm.totalDiscount")}
                    </Text>
                    <Box className={classes.discountRow}>
                      <SegmentedControl
                        className={classes.discountSeg}
                        value={form.values.discountType || "PERCENTAGE"}
                        data={[
                          { label: "%", value: "PERCENTAGE" },
                          { label: "₮", value: "AMOUNT" },
                        ]}
                        onChange={(v) => {
                          form.setFieldValue("discountType", v);
                          form.setFieldValue("discountValue", undefined);
                        }}
                      />
                      <NumberInput
                        className={classes.amountRight}
                        flex={1}
                        max={(form.values.discountType || "PERCENTAGE") === "PERCENTAGE" ? 100 : 1000000000}
                        placeholder="0"
                        hideControls
                        rightSection={
                          form.values.discountValue ? (
                            <CloseButton
                              className={classes.clearSection}
                              onClick={() => form.setFieldValue("discountValue", undefined)}
                              variant="transparent"
                              size="sm"
                              c="gray.7"
                            />
                          ) : null
                        }
                        styles={{
                          section: { pointerEvents: "all" },
                        }}
                        value={form.values.discountValue ?? ""}
                        onChange={(v) => {
                          if (!form.values.discountType) {
                            form.setFieldValue("discountType", "PERCENTAGE");
                          }
                          form.setFieldValue("discountValue", v === "" ? undefined : v);
                        }}
                        error={form.errors.discountValue}
                      />
                    </Box>
                  </Box>

                  <NumberInput
                    className={classes.amountRight}
                    label={t("productForm.shippingAmount")}
                    placeholder="0.00"
                    hideControls
                    rightSection={
                      form.values.shippingAmount ? (
                        <CloseButton
                          className={classes.clearSection}
                          onClick={() => form.setFieldValue("shippingAmount", undefined)}
                          variant="transparent"
                          size="sm"
                          c="gray.7"
                        />
                      ) : null
                    }
                    styles={{
                      section: { pointerEvents: "all" },
                    }}
                    {...form.getInputProps("shippingAmount")}
                  />
                </Box>
              </Box>
            </Grid.Col>
            )}

            {!hideSummaryBlock && (
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Paper
                  radius="md"
                  p={0}
                  withBorder
                  m="md"
                  styles={{ root: { overflow: "hidden", borderColor: "var(--mantine-color-gray-2)" } }}>
                  <Stack gap={0}>
                    <Group
                      px="md"
                      py="xs"
                      justify="space-between"
                      bg="gray.0"
                      styles={{ root: { borderBottom: "1px solid var(--mantine-color-gray-2)" } }}>
                      <Group gap={8}>
                        <ReceiptIcon size={14} color="var(--mantine-color-gray-7)" />
                        <Text fz={14} fw={600} c="gray.8" tt="uppercase" lts={0.8}>
                          {t("productForm.summaryHeading")}
                        </Text>
                      </Group>
                      {totalLineCount > 0 && (
                        <Badge size="xs" variant="light" color="gray" radius="sm">
                          {totalLineCount}
                        </Badge>
                      )}
                    </Group>

                    <Stack gap={10} px="md" pt="sm" pb="xs">
                      <Flex justify="space-between" align="baseline">
                        <Text fz="sm" c="gray.8">
                          {t("productForm.summaryItemTotal")}
                        </Text>
                        <Text fz="sm" fw={500} c="gray.9" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {currencyFormat(itemsSubtotal)}
                        </Text>
                      </Flex>
                      <Flex justify="space-between" align="baseline">
                        <Text fz="sm" c="dimmed">
                          {t("productForm.summaryTax")}
                        </Text>
                        <Text fz="sm" fw={500} c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {currencyFormat(taxSum)}
                        </Text>
                      </Flex>
                      <Flex justify="space-between" align="baseline">
                        <Text fz="sm" c="gray.8">
                          {t("productForm.summaryAdditional")}
                        </Text>
                        <Text fz="sm" fw={500} c="gray.9" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {currencyFormat(additionalSum)}
                        </Text>
                      </Flex>
                    </Stack>

                    <Box px="md">
                      <Divider variant="dashed" color="gray.3" />
                    </Box>

                    <Stack gap={10} px="md" py="sm">
                      <Flex justify="space-between" align="baseline">
                        <Group gap={6} align="baseline">
                          <Text fz="sm" c="gray.8">
                            {t("productForm.summaryDiscount")}
                          </Text>
                          {form.values.discountType === "PERCENTAGE" && form.values.discountValue ? (
                            <Badge size="xs" variant="light" color="red" radius="sm">
                              {form.values.discountValue}%
                            </Badge>
                          ) : null}
                        </Group>
                        <Text
                          fz="sm"
                          fw={500}
                          c={discountSum > 0 ? "red.8" : "gray.9"}
                          style={{ fontVariantNumeric: "tabular-nums" }}>
                          {discountSum > 0 ? "− " : ""}
                          {currencyFormat(discountSum)}
                        </Text>
                      </Flex>
                      <Flex justify="space-between" align="baseline">
                        <Text fz="sm" c="gray.8">
                          {t("productForm.summaryShipping")}
                        </Text>
                        <Text fz="sm" fw={500} c="gray.9" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {currencyFormat(shippingSum)}
                        </Text>
                      </Flex>
                    </Stack>

                    <Group
                      px="md"
                      py="md"
                      justify="space-between"
                      align="center"
                      styles={{
                        root: {
                          background:
                            "linear-gradient(135deg, var(--mantine-color-brand-0), color-mix(in srgb, var(--mantine-color-brand-1) 70%, transparent))",
                          borderTop: "1px solid color-mix(in srgb, var(--mantine-color-brand-3) 60%, transparent)",
                        },
                      }}>
                      <Group gap="sm" wrap="nowrap">
                        <Flex
                          w={34}
                          h={34}
                          bdrs="sm"
                          bg="brand.6"
                          align="center"
                          justify="center"
                          style={{ boxShadow: "0 4px 10px color-mix(in srgb, var(--mantine-color-brand-6) 35%, transparent)" }}>
                          <CoinsIcon size={18} color="var(--mantine-color-white)" weight="fill" />
                        </Flex>
                        <Box>
                          <Text fz={14} fw={600} c="gray.9" tt="uppercase" lts={1}>
                            {t("productForm.summaryTotal")}
                          </Text>
                          <Text fz={14} c="dimmed" lh={1.3}>
                            {t("productForm.summaryLineCount", { count: totalLineCount })}
                          </Text>
                        </Box>
                      </Group>
                      <Text
                        fz="xl"
                        fw={650}
                        c="gray.9"
                        style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                        {currencyFormat(grandTotal)}
                      </Text>
                    </Group>

                    <Flex px="md" py="xs" justify="space-between" align="baseline">
                      <Text fz="sm" c="dimmed">
                        {t("productForm.summaryVat")}
                      </Text>
                      <Text fz="sm" fw={500} c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {currencyFormat(vatSum)}
                      </Text>
                    </Flex>
                  </Stack>
                </Paper>
              </Grid.Col>
            )}
          </Grid>
        )}
      </Flex>
      <VariantSelectorDrawer
        opened={action[0] === "add" && !!form.values?.receiverBusinessId}
        onClose={() => setAction([])}
        businessId={form.values?.receiverBusinessId || ""}
        buyerBusinessId={form.values?.receiverBusinessId || null}
        buyerName={buyerName}
        selectedIds={(form.values?.lines || []).filter((l: any) => !l._removed).map((l: any) => l.variantId)}
        selectedSeed={(form.values?.lines || [])
          .filter((l: any) => !l._removed && l.variantId)
          .map((l: any) => ({
            id: l.variantId,
            nameMon: l.nameMon,
            name: l.name,
            skuCode: l.skuCode,
            image: l.image,
            price: l.price,
            unit: l.unit,
            baseUnit: l.baseUnit,
            category: l.category,
            optionValueNames: l.optionValueNames,
          }))}
        onAdd={(variant) => {
          form.setFieldValue("lines", [
            ...(form.values?.lines || []),
            isAmend ? { ...variantToLine(variant), _isNew: true } : variantToLine(variant),
          ]);
        }}
        onAddAll={(variants) => {
          form.setFieldValue("lines", [
            ...(form.values?.lines || []),
            ...variants.map((v) => (isAmend ? { ...variantToLine(v), _isNew: true } : variantToLine(v))),
          ]);
        }}
      />
    </Box>
  );
});

/** Skip re-render when only unrelated form fields change (e.g. amend reason/description typing). */
export const InvoiceProductForm = memo(
  InvoiceProductFormInner,
  (prev, next) =>
    prev.mode === next.mode &&
    prev.hideSummary === next.hideSummary &&
    prev.hideAdjustments === next.hideAdjustments &&
    prev.form.values.lines === next.form.values.lines &&
    prev.form.values.additionalLines === next.form.values.additionalLines &&
    prev.form.values.discountType === next.form.values.discountType &&
    prev.form.values.discountValue === next.form.values.discountValue &&
    prev.form.values.shippingAmount === next.form.values.shippingAmount &&
    prev.form.values.receiverBusinessId === next.form.values.receiverBusinessId &&
    prev.originalLines === next.originalLines &&
    prev.originalAdditionalLines === next.originalAdditionalLines,
) as typeof InvoiceProductFormInner;

function LineMarkerBadge({
  marker,
  changedFields,
}: {
  marker: LineMarker;
  changedFields?: ChangedField[];
}) {
  const { t } = useTranslation("invoice");
  if (!marker) return null;
  let label =
    marker === "new" ? t("amend.lineNew") : marker === "removed" ? t("amend.lineRemoved") : t("amend.lineChanged");
  if (marker === "changed" && changedFields && changedFields.length > 0) {
    const fieldLabels = changedFields.map((f) => t(`amend.changedField.${f}`)).join("|");
    label = t("amend.lineChangedWithFields", { fields: fieldLabels });
  }
  const color = marker === "new" ? "green" : marker === "removed" ? "red" : "yellow";
  return (
    <Badge size="xs" variant="light" color={color} radius="xl" className={classes.markerBadge} data-marker={marker}>
      {label}
    </Badge>
  );
}

function PreviousHint({ value }: { value: string | number }) {
  return (
    <Text ta="right" className={classes.wasValue}>
      {value}
    </Text>
  );
}

function useCombinedHeader({
  form,
  onClick,
  warehouseOptions,
  isAmend,
  originalLines,
  originalAdditionalLines,
  col,
}: {
  form: UseFormReturnType<any>;
  onClick: (e: any, e1: CombinedRow) => void;
  warehouseOptions: { value: string; label: string }[];
  isAmend: boolean;
  originalLines: any[];
  originalAdditionalLines: any[];
  col: typeof CREATE_COL | typeof AMEND_COL;
}): LineColumn[] {
  const { t } = useTranslation("invoice");
  const inputStyles = {
    input: {
      height: 32,
      minHeight: 32,
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 400,
      fontVariantNumeric: "tabular-nums" as const,
    },
  };
  const changedInputStyles = {
    input: {
      ...inputStyles.input,
      borderColor: "var(--mantine-color-yellow-6)",
    },
  };

  return [
    {
      key: "actions",
      title: <span className={classes.srOnly}>{t("productForm.colActions")}</span>,
      sticky: !isAmend,
      align: "left",
      width: col.actions,
      render: (record: CombinedRow) =>
        isAmend && record?._removed ? (
          <Tooltip label={t("amend.restoreLine")}>
            <ActionIcon
              onClick={() => onClick("restore", record)}
              color="red"
              variant="subtle"
              size={28}
              className={classes.deleteBtn}
              aria-label={t("amend.restoreLine")}
            >
              <ArrowCounterClockwiseIcon size={16} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <ActionIcon
            onClick={() => onClick("remove", record)}
            variant="subtle"
            color="gray"
            size={28}
            className={classes.deleteBtn}
          >
            <TrashIcon size={16} />
          </ActionIcon>
        ),
    },
    {
      key: "index",
      title: "#",
      width: col.index,
      render: (_r, index) => <Text className={classes.rowIndex}>{index + 1}</Text>,
    },
    {
      key: "product",
      title: t("productForm.colProductShort"),
      render: (record: CombinedRow) => {
        const idx = record._sourceIndex;
        if (record._kind === "additional") {
          const marker = isAmend ? deriveLineMarker(record, originalAdditionalLines, "additional") : null;
          return (
            <Box className={classes.cellInput}>
              <TextInput
                size="sm"
                placeholder={t("productForm.colProductShort")}
                disabled={isAmend && record?._removed}
                styles={inputStyles}
                rightSectionWidth={marker ? 168 : 140}
                rightSection={
                  <Group gap={4} wrap="nowrap" justify="flex-end" pr={4}>
                    <Badge size="xs" variant="light" color="gray" radius="sm" className={classes.manualPill}>
                      {t("productForm.manualLinePill")}
                    </Badge>
                    {isAmend && <LineMarkerBadge marker={marker} />}
                  </Group>
                }
                {...form.getInputProps(`additionalLines.${idx}.name`)}
              />
            </Box>
          );
        }
        const marker = isAmend ? deriveLineMarker(record, originalLines, "product") : null;
        const original = isAmend ? findOriginalLine(record, originalLines) : undefined;
        const changedFields = marker === "changed" && original ? getChangedFields(record, original) : undefined;
        const fullName = record?.nameMon || record?.name || "-";
        return (
          <Box miw={0} style={{ overflow: "hidden" }}>
            <Text
              className={classes.productName}
              title={fullName}
              td={record?._removed ? "line-through" : undefined}
              c={record?._removed ? "gray.7" : undefined}
            >
              {fullName}
            </Text>
            <Group gap={6} mt={3} wrap="wrap">
              <Text className={classes.sku} c={record?._removed ? "gray.7" : undefined}>
                {record?.skuCode || "-"}
              </Text>
              {isAmend && <LineMarkerBadge marker={marker} changedFields={changedFields} />}
            </Group>
          </Box>
        );
      },
    },
    {
      key: "warehouse",
      title: t("productForm.colWarehouseUnit"),
      width: col.warehouse,
      render: (record: CombinedRow) => {
        const idx = record._sourceIndex;
        if (record._kind === "additional") {
          return (
            <Box className={classes.cellInput}>
              <TextInput
                size="sm"
                placeholder={t("productForm.colUnit")}
                disabled={isAmend && record?._removed}
                styles={inputStyles}
                {...form.getInputProps(`additionalLines.${idx}.unit`)}
              />
            </Box>
          );
        }
        const inventoryOptions =
          record.inventories?.map((inv: any) => ({
            value: inv.warehouseId,
            label: inv.warehouseName,
          })) ?? [];
        const byId = new Map<string, { value: string; label: string }>();
        for (const opt of warehouseOptions) byId.set(opt.value, opt);
        for (const opt of inventoryOptions) byId.set(opt.value, opt);
        const mergedOptions = Array.from(byId.values());
        const currentValue = form.values?.lines?.[idx]?.warehouseId ?? inventoryOptions[0]?.value ?? null;
        const currentLabel = mergedOptions.find((o) => o.value === currentValue)?.label ?? "";
        const original = isAmend ? findOriginalLine(record, originalLines) : undefined;
        const origWh = original?.warehouseId ?? "";
        const whChanged = isAmend && original && String(origWh) !== String(currentValue ?? "");
        const origLabel = whChanged ? mergedOptions.find((o) => o.value === origWh)?.label ?? origWh : null;

        return (
          <Stack gap={2} className={`${classes.cellInput} ${classes.whSelect}`}>
            <Tooltip label={currentLabel || "-"} withArrow disabled={!currentLabel} position="top-start">
              <Box>
                <Select
                  size="sm"
                  data={mergedOptions}
                  value={currentValue}
                  onChange={(v) => form.setFieldValue(`lines.${idx}.warehouseId`, v)}
                  allowDeselect={false}
                  searchable
                  disabled={isAmend && record?._removed}
                  w="100%"
                  styles={whChanged ? changedInputStyles : inputStyles}
                  comboboxProps={{ width: "max-content" }}
                />
              </Box>
            </Tooltip>
            {origLabel ? <PreviousHint value={origLabel} /> : null}
          </Stack>
        );
      },
    },
    {
      key: "qty",
      title: t("productForm.colQty"),
      align: "right",
      width: col.qty,
      render: (record: CombinedRow) => {
        const idx = record._sourceIndex;
        const path =
          record._kind === "additional" ? `additionalLines.${idx}.quantity` : `lines.${idx}.quantity`;
        const original =
          record._kind === "product" && isAmend ? findOriginalLine(record, originalLines) : undefined;
        const showOldQty =
          !!original && Number(original.quantity ?? 0) !== Number(record.quantity ?? 0) && !record?._removed;
        return (
          <Stack gap={2} align="flex-end" className={classes.cellInput}>
            <NumberInput
              className={classes.qtyInput}
              style={{ width: col.qtyInput }}
              max={9000000}
              min={0}
              size="sm"
              placeholder="0"
              hideControls
              disabled={isAmend && record?._removed}
              styles={{
                input: {
                  ...(showOldQty ? changedInputStyles.input : inputStyles.input),
                  textAlign: "right",
                  width: col.qtyInput,
                },
              }}
              {...form.getInputProps(path)}
            />
            {showOldQty && <PreviousHint value={original.quantity} />}
          </Stack>
        );
      },
    },
    {
      key: "unitPrice",
      title: t("productForm.colUnitPrice"),
      align: "right",
      width: col.unitPrice,
      render: (record: CombinedRow) => {
        const idx = record._sourceIndex;
        if (record._kind === "additional") {
          return (
            <Box className={classes.cellInput}>
              <NumberInput
                size="sm"
                placeholder="0"
                hideControls
                disabled={isAmend && record?._removed}
                styles={{
                  input: {
                    ...inputStyles.input,
                    textAlign: "right",
                  },
                }}
                {...form.getInputProps(`additionalLines.${idx}.price`)}
              />
            </Box>
          );
        }
        const original = isAmend ? findOriginalLine(record, originalLines) : undefined;
        const showOldPrice =
          !!original && Number(original.price ?? 0) !== Number(record.price ?? 0) && !record?._removed;
        return (
          <Stack gap={2} align="flex-end">
            <Text
              className={classes.unitPrice}
              td={record?._removed ? "line-through" : undefined}
              c={record?._removed ? "gray.7" : undefined}
            >
              {currencyFormat(record?.price ?? 0, { withSymbol: false })}
            </Text>
            {showOldPrice && <PreviousHint value={currencyFormat(original.price ?? 0, { withSymbol: false })} />}
          </Stack>
        );
      },
    },
    {
      key: "amount",
      title: t("productForm.colAmount"),
      align: "right",
      width: col.amount,
      render: (record: CombinedRow) => {
        const rowTotalVal = lineTotal(record);
        if (record._kind === "additional") {
          return (
            <Text
              className={classes.amount}
              td={record?._removed ? "line-through" : undefined}
              c={record?._removed ? "gray.7" : undefined}
            >
              {currencyFormat(rowTotalVal, { withSymbol: false })}
            </Text>
          );
        }
        const totalVat = lineVat(record);
        const totalTax = record?.taxAmount ?? 0;
        const original = isAmend ? findOriginalLine(record, originalLines) : undefined;
        const oldTotal = original ? lineTotal(original) : null;
        const showOldTotal = oldTotal != null && oldTotal !== rowTotalVal && !record?._removed;

        return (
          <Stack gap={2} align="flex-end">
            <Tooltip
              label={
                <Box>
                  <Text size="xs">
                    {t("detail.totals.vat")}: {currencyFormat(totalVat, { withSymbol: false })}
                  </Text>
                  <Text size="xs">
                    {t("detail.totals.tax")}: {currencyFormat(totalTax, { withSymbol: false })}
                  </Text>
                </Box>
              }
              withArrow
              disabled={totalVat === 0 && totalTax === 0}
            >
              <Text
                className={classes.amount}
                td={record?._removed ? "line-through" : undefined}
                c={record?._removed ? "gray.7" : undefined}
                style={{ cursor: "default" }}
              >
                {currencyFormat(rowTotalVal, { withSymbol: false })}
              </Text>
            </Tooltip>
            {showOldTotal && <PreviousHint value={currencyFormat(oldTotal, { withSymbol: false })} />}
          </Stack>
        );
      },
    },
  ];
}
